import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Link,
  Modal,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ndcBg from "../../assets/images/ndc-bg.png";
import closeIcon from "../../assets/images/close.png";
import xlFile from "../../utils/sample.xlsx";
import { useDispatch } from "react-redux";
import { importAdminUsers } from "../../redux/slices/userSlice";

const ImportUsersModal = ({ open, handleClose }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState("");
  const [importSummary, setImportSummary] = useState(null);
  const [errorReportURL, setErrorReportURL] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const handleCancel = () => {
    setFile(null);
    setFileError("");
    setErrorReportURL(null);
    setImportSummary(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    handleClose();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (!selectedFile.name.endsWith(".xlsx")) {
        setFile(null);
        setFileError("Only .xlsx files are allowed!");
        e.target.value = null;
      } else {
        setFileError("");
        setFile(selectedFile);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith(".xlsx")) {
        setFile(droppedFile);
        setFileError("");
      } else {
        setFile(null);
        setFileError("Only .xlsx files are allowed!");
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleModalClose = () => {
    setFile(null);
    setFileError("");
    setImportSummary(null);
    setErrorReportURL(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    handleClose();
  };

  // Show summary section when Import Users is clicked
  const handleImport = () => {
    dispatch(
      importAdminUsers({
        importedFile: file,
        downloadLink: setErrorReportURL,
        summary: setImportSummary,
      })
    );
  };

  // Dummy handler for error download
  const handleDownloadErrors = () => {
    const link = document.createElement("a");
    link.href = errorReportURL;
    link.download = "errors-report.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Reset button text and clean up
    setTimeout(() => {
      if (errorReportURL) {
        URL.revokeObjectURL(errorReportURL);
      }
    }, 1000);
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="import-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "52%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: { xs: "95vw", md: "90vw", lg: "55rem" },
          maxWidth: "100vw",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `url(${ndcBg})`,
            minHeight: { xs: "4rem", sm: "6rem", md: "8rem" },
            borderRadius: "1rem",
            backgroundRepeat: "round",
            px: { xs: "1rem", sm: "1.5rem", md: "2rem" },
            justifyContent: "space-between",
            alignItems: "center",
            display: "flex",
          }}
        >
          <Typography
            id="import-modal-title"
            variant="h1"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.25rem" },
              fontWeight: 700,
              lineHeight: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontFamily: "Open Sans",
            }}
          >
            Import New Users
          </Typography>
          <IconButton
            onClick={handleModalClose}
            sx={{ height: "2.5rem", width: "2.5rem" }}
          >
            <img src={closeIcon} alt="close icon" />
          </IconButton>
        </Box>

        {/* Body: show summary if importSummary exists, else show upload UI */}
        {importSummary ? (
          <Box
            px={{ xs: 2, sm: 4, md: 5 }}
            py={{ xs: 3, sm: 5, md: 8 }}
            textAlign="center"
          >
            <Typography variant="h5" color="success.main" mb={2}>
              {importSummary.successCount} user(s) imported successfully.
            </Typography>
            {importSummary.errorCount > 0 && (
              <Typography variant="h6" color="error" mb={2}>
                {importSummary.errorCount} row(s) had errors Please click&nbsp;
                <Button
                  variant="text"
                  color="error"
                  onClick={handleDownloadErrors}
                  sx={{
                    textTransform: "none",
                    p: 0,
                    mb: "5px",
                    minWidth: 0,
                    textDecoration: "underline",
                    fontWeight: "500",
                    fontSize: "1.25rem",
                    fontFamily: "Open Sans",
                  }}
                >
                  here
                </Button>
                &nbsp;to review.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {/* Section 1 - Download Sample */}
            <Box
              my={3}
              px={{ xs: 2, sm: 4, md: 5 }}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Link
                href={xlFile}
                download
                underline="none"
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: "600",
                  fontSize: "1rem",
                }}
              >
                {" "}
                Download sample sheet to import new users
                <IconButton color="primary">
                  <DownloadIcon />
                </IconButton>
              </Link>
            </Box>

            {/* Section 2 - Drag & Drop / File Upload */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: "2px dashed",
                borderColor: dragActive ? "primary.main" : "grey.400",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                textAlign: "center",
                mx: { xs: 1, sm: 3, md: 5 },
                mb: { xs: 2, sm: 3, md: 5 },
                bgcolor: dragActive ? "grey.100" : "transparent",
                cursor: "pointer",
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: "600",
                  fontSize: "1rem",
                }}
              >
                Drag & Drop your .xlsx file here
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: "600",
                  fontSize: "1rem",
                }}
              >
                or
              </Typography>
              <Button
                variant="outlined"
                component="label"
                sx={{
                  mt: 1,
                  textTransform: "none",
                  px: 3,
                  fontFamily: "Open Sans",
                  fontWeight: "600",
                  fontSize: "1rem",
                }}
              >
                Choose File
                <input
                  hidden
                  accept=".xlsx"
                  type="file"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </Button>
              {file && (
                <Typography variant="body2" mt={2}>
                  Selected: {file.name}
                </Typography>
              )}
              {fileError && (
                <Typography variant="body2" color="error" mt={2}>
                  {fileError}
                </Typography>
              )}
            </Box>

            {/* Section 3 - Actions */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "column", md: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="flex-end"
              gap={2}
              sx={{
                px: { xs: 2, sm: 4, md: 5 },
                pb: { xs: 2, sm: 3, md: 5 },
              }}
            >
              <Button
                onClick={handleCancel}
                sx={{
                  width: { xs: "100%", sm: "100%", md: "auto" },
                  textTransform: "none",
                  borderRadius: "24px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  fontFamily: "Open Sans",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={!file}
                sx={{
                  width: { xs: "100%", sm: "100%", md: "auto" },
                  textTransform: "none",
                  borderRadius: "1.5rem",
                  fontWeight: "700",
                  fontSize: "1rem",
                  fontFamily: "Open Sans",
                }}
              >
                Import Users
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ImportUsersModal;
